<template>
  <div class="tabs-selection-container">
    <div v-if="!franchisee" class="tabs-selection-header">
      <mf-tabs-menu v-model="tabValue" :tabs="tabsSelectionOptions" />
    </div>
    <div class="tabs-selection-content">
      <offers-table
        v-if="showTabContent('offer-schedule-selection') || showTabContent('erp-offer-schedule-selection')"
        :key="`rendered-offers-table-${tab}`"
        :franchisee="franchisee"
        :search="search"
        :disabled="disabled"
        :erp-offers="showTabContent('erp-offer-schedule-selection')"
      />
      <products-and-offers-container
        v-if="showTabContent('import-offers') || showTabContent('search-for-products')"
        :tab="tab"
        :query-selections="querySelections"
        :set-item="setItem"
        :remove-group="removeGroup"
        :remove-item="removeItem"
        :clear-all="clearAll"
        :disabled="isMariaCampaignFinished"
        :initially-products="initiallyProducts"
      >
        <template #import-and-search-content>
          <div class="import-and-search-content">
            <import-offers v-if="showTabContent('import-offers')" />
            <search-products
              v-if="showTabContent('search-for-products')"
              :loading="loading"
              :query-selections="querySelections"
              :products-groups="productsGroups"
              :products="localSearchedProducts"
              :set-item="setItem"
              :set-group="setGroup"
            />
          </div>
        </template>
      </products-and-offers-container>
    </div>
  </div>
</template>

<script>
import ProductsAndOffersContainer from './ProductsAndOffersContainer.vue'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'

import OffersTable from './OffersTable.vue'
import ImportOffers from './ImportOffers.vue'
import SearchProducts from './SearchProducts.vue'
import { mariaCampaign } from '../../../mixins/maria-campaign'
import { QUERY_GET_PRODUCTS_AND_GROUPS } from '../../../graphql'

export default {
  name: 'TabsSelection',
  components: {
    OffersTable,
    ImportOffers,
    SearchProducts,
    ProductsAndOffersContainer,
  },
  mixins: [mariaCampaign],
  props: {
    tab: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    franchisee: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    initiallyProducts: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    productsGroups: [],
    localSearchedProducts: [],
    allSearchedProducts: [],
    allSearchedGroups: [],
  }),
  computed: {
    tabValue: {
      get() {
        return this.tab
      },
      set(value) {
        this.$emit('update:tab', value)
      },
    },
    importedItems: {
      get() {
        return this.importedProducts
      },
      set(value) {
        this.$store.commit('SET_IMPORTED_PRODUCTS', value)
      },
    },
    searchedItems: {
      get() {
        return this.searchedProducts
      },
      set(value) {
        this.$store.commit('SET_SEARCHED_PRODUCTS', value)
      },
    },
    offerSalesItems: {
      get() {
        return this.campaign.offer_sales
      },
      set(value) {
        this.setOmniCampaignKey({
          key: 'offer_sales',
          payload: value,
        })
      },
    },
    hasSearchedOrImportedProducts() {
      return Boolean(this.searchedItems?.length || this.importedItems?.length)
    },
    useErpSalesAsOffersSchedules() {
      return this.$getAllowedModules()['use_erp_sales_as_offers_schedules']
    },
    tabsSelectionOptions() {
      return [
        ...(this.hasOfferSalesPermission
          ? [
              {
                name: 'Gestão de ofertas',
                tabPermission: 'campanhas/gestao_ofertas/ofertas',
                itemsLength: 0,
                haveLength: false,
                disabled: this.hasSearchedOrImportedProducts,
                key: 'offer-schedule-selection',
              },
            ]
          : []),
        ...(this.hasOfferSalesPermission && this.useErpSalesAsOffersSchedules
          ? [
              {
                name: 'Gestão de ofertas do ERP',
                tabPermission: 'campanhas/gestao_ofertas/ofertas',
                itemsLength: 0,
                haveLength: false,
                disabled: this.hasSearchedOrImportedProducts,
                key: 'erp-offer-schedule-selection',
              },
            ]
          : []),
        {
          name: 'Importar ofertas',
          itemsLength: 0,
          haveLength: false,
          disabled: this.campaign?.offer_schedules.length > 0,
          key: 'import-offers',
        },
        {
          name: 'Buscar produtos',
          itemsLength: 0,
          haveLength: false,
          disabled: this.campaign?.offer_schedules.length > 0,
          key: 'search-for-products',
        },
      ]
    },
  },
  mounted() {
    if (this.campaign?.offer_sales?.length > 0) {
      this.$store.commit('SET_SEARCHED_PRODUCTS', this.formatOfferSales(this.campaign?.offer_sales))
    }
    if (this.campaign?.offer_schedules?.length > 0) {
      if (this.campaign.offer_schedules.some((offer) => offer.origin !== 'erp')) {
        this.tabValue = 0
      } else {
        this.tabValue = 1
      }
    }
    if (this.hasSearchedOrImportedProducts && this.hasOfferSalesPermission) {
      this.tabValue = 2
    }
  },
  methods: {
    showTabContent(key) {
      return this.tabsSelectionOptions.findIndex((tab) => tab.key === key) === this.tab
    },
    querySelections(query) {
      this.searchValue = query
      if (!query || query.length < 3) return
      this.loading = true
      const self = this
      window.clearTimeout(this.timeout)
      this.timeout = window.setTimeout(() => {
        self.searchItem(query)
      }, 500)
    },
    searchItem(query) {
      const variables = { search: query }
      if (this.onlyActive) {
        variables.active = true
      }
      this.$apollo
        .query({
          query: QUERY_GET_PRODUCTS_AND_GROUPS,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then(({ data: { products: { products } = {}, productsGroups: { productsGroups } } = {} }) => {
          this.allSearchedProducts = products
          this.allSearchedGroups = productsGroups
          this.productsGroups = this.removeChosenGroups(productsGroups)
          this.localSearchedProducts = this.removeChosenItems(products)
          this.loading = false
        })
    },
    removeChosenGroups(responseData) {
      const array = responseData
      return array.reduce((accumulator, current) => {
        const wasChose = this.searchedProducts.map((product) => product.group_name).includes(current.name)

        if (!wasChose) {
          accumulator.push(current)
        }

        return accumulator
      }, [])
    },
    removeChosenItems(responseData) {
      const array = responseData
      const allChosenProducts = this.searchedProducts.concat(this.importedProducts)
      for (let item of allChosenProducts) {
        let itemIndex = array.findIndex((product) => product.sku === item.sku)
        if (itemIndex > -1) {
          array.splice(itemIndex, 1)
        }
      }
      return array
    },
    setGroup(group) {
      const itemGroup = find(this.productsGroups, {
        _id: group._id,
      })

      if (!itemGroup) return
      const itemGroupIndex = findIndex(this.productsGroups, itemGroup)
      const { products, rules_exhibition, name, image_url, ...groupInfo } = this.productsGroups[itemGroupIndex]
      const productsFormatted = products.map((product) => ({
        isGroup: true,
        group_name: name,
        groupInfo,
        group_rules_exhibition: rules_exhibition,
        group_image_url: image_url,
        ...product,
      }))
      this.productsGroups.splice(itemGroupIndex, 1)
      let pushList = this.searchedItems
      pushList.push(...productsFormatted)
      this.searchedItems = pushList.map((item) => {
        return {
          ...item,
          price_1: 0,
          price_2: 0,
          active: true,
          product: {
            ean: item.ean,
            eans: item.eans,
            sku: item.sku,
          },
        }
      })
    },
    clearAll() {
      this.loading = true
      this.importedItems = []
      this.searchedItems = []
      this.offerSalesItems = []

      if (this.searchValue) this.searchItem(this.searchValue)
      else this.loading = false
    },
    hasArrayItem(array, item) {
      let isFromArray = false
      array.map((arrayItem) => {
        if (item._id === arrayItem._id) {
          isFromArray = true
        }
      })
      return isFromArray
    },
    removeItemFromArray(array, item) {
      if (!item) return array

      const { isGroup, groupInfo, _id, product } = item
      const itemId = _id || product?._id

      return array.filter((arrayItem) => {
        if (arrayItem?.offer_sales) {
          arrayItem.offer_sales = arrayItem.offer_sales.filter((offerSalesItem) => offerSalesItem?.product?._id !== itemId)
          return arrayItem.offer_sales.length > 0
        }

        const { _id: productId, groupInfo: productGroup, product: arrayProduct } = arrayItem
        const arrayItemId = productId || arrayProduct?._id

        if (isGroup && groupInfo) {
          return arrayItemId !== itemId
        }

        if (isGroup && !groupInfo) {
          return productGroup?._id !== itemId
        }

        if (!isGroup) {
          return arrayItemId !== itemId
        }

        return true
      })
    },
    removeItem(item) {
      this.searchedItems = this.removeItemFromArray(this.searchedItems, item)
      this.importedItems = this.removeItemFromArray(this.importedItems, item)
      this.offerSalesItems = this.removeItemFromArray(this.offerSalesItems, item)

      if (this.hasArrayItem(this.allSearchedProducts, item)) this.localSearchedProducts.push(item)
    },
    addItemToArray(array, item) {
      if (!item.isGroup) {
        array.sort((a, b) => this.sortList(a, b)).push(item)
      }
      return array.map((arrayItem) => {
        return {
          ...arrayItem,
          price_1: 0,
          price_2: 0,
          product: {
            ean: arrayItem.ean,
            eans: arrayItem.eans,
            sku: arrayItem.sku,
          },
        }
      })
    },
    setItem(item) {
      const { sku } = item
      const itemObject = find(this.localSearchedProducts, {
        sku,
      })
      if (!itemObject) return

      this.localSearchedProducts = this.removeItemFromArray(this.localSearchedProducts, itemObject)

      this.searchedItems = this.addItemToArray(this.searchedItems, itemObject)
    },
    sortList(a, b) {
      if (a.name > b.name) return 1
      if (a.name < b.name) return -1
      return 0
    },
    removeGroup(group) {
      this.searchedItems = this.removeItemFromArray(this.searchedItems, group)
      this.importedItems = this.removeItemFromArray(this.importedItems, group)
      this.offerSalesItems = this.removeItemFromArray(this.offerSalesItems, group)

      if (this.hasArrayItem(this.allSearchedGroups, group) && !this.hasArrayItem(this.productsGroups, group)) this.productsGroups.push(group)
    },
  },
}
</script>

<style lang="scss" scoped>
.import-and-search-content {
  max-height: 600px;
}
</style>
